<script setup lang="ts">
import type { BlockList } from '@/models'
import { type PropType } from 'vue'
import FlexibleBlock from '../../blockStyles/FlexibleBlock.vue'
import { mainBackgroundClasses } from '@/constants'

const props = defineProps({
  blockList: {
    type: Object as PropType<BlockList>,
    required: true,
  },
  isHeaderBlock: {
    type: Boolean,
    default: false,
  },
})

/**
 * Generates border styling for grid items based on their position
 * Creates a grid with borders between items but not on outer edges
 * Used only for 'subStyle' 'one'
 * @param index - 0-based index of the item
 */
function gridBorderStyling(index: number): string {
  // Borders only apply to subStyle 'one'
  if (props.blockList.subStyle !== 'one') return ''

  const columns = props.blockList.blocks.length > 3 ? 4 : props.blockList.blocks.length
  const position = index + 1 // Convert to 1-based for easier calculations
  const row = Math.ceil(position / columns)
  const col = ((position - 1) % columns) + 1

  const isLastColumn = col === columns
  const isFirstRow = row === 1

  const classes = ['border-dashed']

  if (columns > 0) {
    // First row: add black border and bottom border
    if (isFirstRow) classes.push('border-black', 'border-b')
    // Other rows: remove bottom border
    else classes.push('border-b-0')

    // Add right border for all items except last column
    if (!isLastColumn) classes.push('border-black', 'border-r')
  }

  return classes.join(' ')
}
</script>

<template>
  <div :class="mainBackgroundClasses(blockList.details.backgroundColour)">
    <FlexibleBlock
      :blockData="blockList.details"
      :isHeaderBlock="isHeaderBlock"
      :customClass="mainBackgroundClasses(blockList.details.backgroundColour)"
    />
    <div
      class="grid min-h-48"
      :class="{
        'grid-cols-4 gap-0.5': blockList.subStyle === 'one',
        'grid-cols-2 gap-0.5': blockList.subStyle === 'two',
        'grid-cols-2 gap-2 max-sm:grid-cols-1': blockList.subStyle === 'three',
        'grid-cols-4 max-md:grid-cols-3 max-sm:grid-cols-2': blockList.subStyle === 'four',
        'grid-cols-5 gap-0.5 max-sm:grid-cols-1': blockList.subStyle === 'five',
      }"
    >
      <template v-for="(block, index) in blockList.blocks" :key="index">
        <div class="flex flex-col text-center p-2 items-center" :class="gridBorderStyling(index)">
          <FlexibleBlock
            :subStyle="blockList.subStyle"
            :blockData="block"
            :isHeaderBlock="false"
            :customClass="`size-full grid-style grid-style-${blockList.subStyle}`"
          />
        </div>
      </template>
    </div>
  </div>
</template>
